import React from 'react';
import moment from 'moment-timezone';
import { flatMap } from 'lodash';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Columns2 from 'components/Columns2';
import Container from 'components/Container';
import { CoachingSessionFrequency } from 'coaching/CoachingConstants';
import GetFullCoachScheduleContainer, {
  CoachAvailabilityException,
  CoachUpcomingSession,
} from 'coaching/containers/GetFullCoachScheduleContainer';
import MediaObject from 'components/MediaObject';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Text from 'components/Text';
import { constructAvailabilitiesByDay } from 'coaching/lib/coachAvailability';
import { CoachAvailabilityInput, CoachingEventDefinitionInput } from 'globalTypes';

type Props = {
  coachUuid?: string;
  coachName: string;
  proposedEventTimeframe: CoachingEventDefinitionInput;
  unavailableDates?: string[];
  coachIsFullyAvailable?: boolean;
  eventFrequency: CoachingSessionFrequency;
  loading: boolean;
  relativeUtcOffset?: number;
};

export default function CoachingCoordinatorAvailabilityConflicts(props: Props) {
  if (props.loading) {
    return <CenteredLoadingIndicator />;
  }

  const { unavailableDates, coachUuid, proposedEventTimeframe, relativeUtcOffset } = props;

  if (props.coachIsFullyAvailable === undefined || unavailableDates === undefined) {
    return null;
  }

  const showDates = unavailableDates?.length > 0 && props.eventFrequency !== 'once';

  function relativeMoment(...args: string[]) {
    return moment(...args).add(relativeUtcOffset ?? 0, 'minutes');
  }

  const unavailabilityText = showDates ? (
    <Text weight="semibold">
      {unavailableDates.map(date => moment(date).format('MMM D')).join(', ')}.
    </Text>
  ) : (
    'at the selected timeslot.'
  );

  function renderHeading(text: string) {
    return (
      <Row size="xsmall">
        <Text color="thunder" weight="semibold">
          {text}
        </Text>
      </Row>
    );
  }

  function renderSingleDayRow(originalStartDateTime: moment.Moment, endDateTime?: moment.Moment) {
    return (
      <Row size="xsmall" key={originalStartDateTime.toISOString()} data-test="single-day-row">
        <MediaObject>
          <Text weight="semibold">{originalStartDateTime.format('ddd, MMM D')}</Text>
          <Text>
            {originalStartDateTime.format('h:mm A')}
            {endDateTime && ` — ${endDateTime.format('h:mm A')}`}
          </Text>
        </MediaObject>
      </Row>
    );
  }

  function renderAvailability(availability: CoachAvailabilityInput) {
    return (
      <MediaObject key={availability.timeSlot.dayOfWeek} data-test="coach-availability-row">
        <Text weight="semibold">
          {moment(availability.timeSlot.dayOfWeek, 'dddd').format('ddd')}
        </Text>
        {availability.timeSlot.intervals.map(interval => {
          return (
            <Row size="xsmall" key={interval.startTime}>
              <Text>{relativeMoment(interval.startTime, 'HH:mm').format('h:mm A')}</Text> —{' '}
              <Text>{relativeMoment(interval.endTime, 'HH:mm').format('h:mm A')}</Text>
            </Row>
          );
        })}
      </MediaObject>
    );
  }

  function renderException(exception: CoachAvailabilityException) {
    const startDateTimeMoment = moment(exception.startDateTime);
    const endDateTimeMoment = moment(exception.endDateTime);
    const isSameDayOfYear = startDateTimeMoment.dayOfYear() === endDateTimeMoment.dayOfYear();
    const isSameYear = startDateTimeMoment.year() === endDateTimeMoment.year();
    const isSingleDayException = isSameDayOfYear && isSameYear;
    return isSingleDayException ? (
      renderSingleDayRow(startDateTimeMoment, endDateTimeMoment)
    ) : (
      <Row size="xsmall" key={exception.startDateTime} data-test="multi-day-exception">
        <Text weight="semibold">
          {startDateTimeMoment.format('ddd, MMM D')} — {endDateTimeMoment.format('ddd, MMM D')}
        </Text>
      </Row>
    );
  }

  function renderAvailabilities(availabilities: CoachAvailabilityInput[]) {
    return (
      <>
        {renderHeading('Availability')}
        {availabilities
          .filter(availability => availability.timeSlot.intervals.length > 0)
          .map(availability => renderAvailability(availability))}
      </>
    );
  }

  function renderExceptions(exceptions: CoachAvailabilityException[]) {
    return (
      <>
        {renderHeading('Unavailable')}
        {exceptions.map(exception => renderException(exception))}
      </>
    );
  }

  function renderUpcomingSessions(sessions: CoachUpcomingSession[]) {
    const sessionDatesForTheNextWeek = flatMap(sessions, session =>
      session.instances.map(instance => instance.startDateTime)
    )
      .filter(dateTime =>
        moment(dateTime).isBefore(moment(proposedEventTimeframe.startDateTime).add(7, 'days'))
      )
      .sort((a, b) => (moment(a).isBefore(b) ? -1 : 1));
    return (
      <>
        {renderHeading('Upcoming sessions')}
        {sessionDatesForTheNextWeek.map(sessionDate => renderSingleDayRow(moment(sessionDate)))}
      </>
    );
  }

  return props.coachIsFullyAvailable ? null : (
    <>
      <Row>
        <Notice color="polar-bear" noShadow noBorderRadius data-test="availability-conflict-notice">
          {props.coachName} is unavailable {unavailabilityText}
        </Notice>
      </Row>
      <GetFullCoachScheduleContainer
        coachUuid={coachUuid}
        startDateTime={proposedEventTimeframe.startDateTime}
        endDateTime={moment
          .utc(proposedEventTimeframe.startDateTime)
          .add(6, 'months')
          .toISOString()}
        timezone={proposedEventTimeframe.timezone}>
        {({ coachAvailabilities, coachAvailabilityExceptions, upcomingSessions }) => {
          const availabilitiesByDay = constructAvailabilitiesByDay(coachAvailabilities);
          return (
            <Container size="medium" align="left">
              <Columns2>
                <div>
                  <Row size="small">{renderUpcomingSessions(upcomingSessions)}</Row>
                  {renderExceptions(coachAvailabilityExceptions)}
                </div>
                <div>{renderAvailabilities(availabilitiesByDay)}</div>
              </Columns2>
            </Container>
          );
        }}
      </GetFullCoachScheduleContainer>
    </>
  );
}
